import _classCallCheck from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/classCallCheck.js";
import _createClass from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/createClass.js";
import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { TRACING_DEFAULTS, addTracingExtensions, startIdleTransaction, getActiveTransaction } from '@sentry/core';
import { logger, tracingContextFromHeaders, getDomElement } from '@sentry/utils';
import { registerBackgroundTabDetection } from './backgroundtab.js';
import { startTrackingWebVitals, startTrackingLongTasks, startTrackingInteractions, addPerformanceEntries } from './metrics/index.js';
import { defaultRequestInstrumentationOptions, instrumentOutgoingRequests } from './request.js';
import { instrumentRoutingWithDefaults } from './router.js';
import { WINDOW } from './types.js';
var BROWSER_TRACING_INTEGRATION_ID = 'BrowserTracing';
/** Options for Browser Tracing integration */

var DEFAULT_BROWSER_TRACING_OPTIONS = _objectSpread(_objectSpread({}, TRACING_DEFAULTS), {}, {
  markBackgroundTransactions: true,
  routingInstrumentation: instrumentRoutingWithDefaults,
  startTransactionOnLocationChange: true,
  startTransactionOnPageLoad: true,
  enableLongTask: true,
  _experiments: {}
}, defaultRequestInstrumentationOptions);
/**
 * The Browser Tracing integration automatically instruments browser pageload/navigation
 * actions as transactions, and captures requests, metrics and errors as spans.
 *
 * The integration can be configured with a variety of options, and can be extended to use
 * any routing library. This integration uses {@see IdleTransaction} to create transactions.
 */


var BrowserTracing = /*#__PURE__*/function () {
  function BrowserTracing(_options) {
    _classCallCheck(this, BrowserTracing);

    BrowserTracing.prototype.__init.call(this);

    BrowserTracing.prototype.__init2.call(this);

    addTracingExtensions();

    if (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) {
      this._hasSetTracePropagationTargets = !!(_options && ( // eslint-disable-next-line deprecation/deprecation
      _options.tracePropagationTargets || _options.tracingOrigins));
    }

    this.options = _objectSpread(_objectSpread({}, DEFAULT_BROWSER_TRACING_OPTIONS), _options); // Special case: enableLongTask can be set in _experiments
    // TODO (v8): Remove this in v8

    if (this.options._experiments.enableLongTask !== undefined) {
      this.options.enableLongTask = this.options._experiments.enableLongTask;
    } // TODO (v8): remove this block after tracingOrigins is removed
    // Set tracePropagationTargets to tracingOrigins if specified by the user
    // In case both are specified, tracePropagationTargets takes precedence
    // eslint-disable-next-line deprecation/deprecation


    if (_options && !_options.tracePropagationTargets && _options.tracingOrigins) {
      // eslint-disable-next-line deprecation/deprecation
      this.options.tracePropagationTargets = _options.tracingOrigins;
    }

    this._collectWebVitals = startTrackingWebVitals();

    if (this.options.enableLongTask) {
      startTrackingLongTasks();
    }

    if (this.options._experiments.enableInteractions) {
      startTrackingInteractions();
    }
  }
  /**
   * @inheritDoc
   */


  _createClass(BrowserTracing, [{
    key: "__init",
    value: // This class currently doesn't have a static `id` field like the other integration classes, because it prevented
    // @sentry/tracing from being treeshaken. Tree shakers do not like static fields, because they behave like side effects.
    // TODO: Come up with a better plan, than using static fields on integration classes, and use that plan on all
    // integrations.

    /** Browser Tracing integration options */

    /**
     * @inheritDoc
     */
    function __init() {
      this.name = BROWSER_TRACING_INTEGRATION_ID;
    }
  }, {
    key: "__init2",
    value: function __init2() {
      this._hasSetTracePropagationTargets = false;
    }
  }, {
    key: "setupOnce",
    value: function setupOnce(_, getCurrentHub) {
      var _this = this;

      this._getCurrentHub = getCurrentHub;
      var hub = getCurrentHub();
      var client = hub.getClient();
      var clientOptions = client && client.getOptions();
      var _this$options = this.options,
          instrumentRouting = _this$options.routingInstrumentation,
          startTransactionOnLocationChange = _this$options.startTransactionOnLocationChange,
          startTransactionOnPageLoad = _this$options.startTransactionOnPageLoad,
          markBackgroundTransactions = _this$options.markBackgroundTransactions,
          traceFetch = _this$options.traceFetch,
          traceXHR = _this$options.traceXHR,
          shouldCreateSpanForRequest = _this$options.shouldCreateSpanForRequest,
          enableHTTPTimings = _this$options.enableHTTPTimings,
          _experiments = _this$options._experiments;
      var clientOptionsTracePropagationTargets = clientOptions && clientOptions.tracePropagationTargets; // There are three ways to configure tracePropagationTargets:
      // 1. via top level client option `tracePropagationTargets`
      // 2. via BrowserTracing option `tracePropagationTargets`
      // 3. via BrowserTracing option `tracingOrigins` (deprecated)
      //
      // To avoid confusion, favour top level client option `tracePropagationTargets`, and fallback to
      // BrowserTracing option `tracePropagationTargets` and then `tracingOrigins` (deprecated).
      // This is done as it minimizes bundle size (we don't have to have undefined checks).
      //
      // If both 1 and either one of 2 or 3 are set (from above), we log out a warning.

      var tracePropagationTargets = clientOptionsTracePropagationTargets || this.options.tracePropagationTargets;

      if ((typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && this._hasSetTracePropagationTargets && clientOptionsTracePropagationTargets) {
        logger.warn('[Tracing] The `tracePropagationTargets` option was set in the BrowserTracing integration and top level `Sentry.init`. The top level `Sentry.init` value is being used.');
      }

      instrumentRouting(function (context) {
        var transaction = _this._createRouteTransaction(context);

        _this.options._experiments.onStartRouteTransaction && _this.options._experiments.onStartRouteTransaction(transaction, context, getCurrentHub);
        return transaction;
      }, startTransactionOnPageLoad, startTransactionOnLocationChange);

      if (markBackgroundTransactions) {
        registerBackgroundTabDetection();
      }

      if (_experiments.enableInteractions) {
        this._registerInteractionListener();
      }

      instrumentOutgoingRequests({
        traceFetch: traceFetch,
        traceXHR: traceXHR,
        tracePropagationTargets: tracePropagationTargets,
        shouldCreateSpanForRequest: shouldCreateSpanForRequest,
        enableHTTPTimings: enableHTTPTimings
      });
    }
    /** Create routing idle transaction. */

  }, {
    key: "_createRouteTransaction",
    value: function _createRouteTransaction(context) {
      var _this2 = this;

      if (!this._getCurrentHub) {
        (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.warn("[Tracing] Did not create ".concat(context.op, " transaction because _getCurrentHub is invalid."));
        return undefined;
      }

      var hub = this._getCurrentHub();

      var _this$options2 = this.options,
          beforeNavigate = _this$options2.beforeNavigate,
          idleTimeout = _this$options2.idleTimeout,
          finalTimeout = _this$options2.finalTimeout,
          heartbeatInterval = _this$options2.heartbeatInterval;
      var isPageloadTransaction = context.op === 'pageload';
      var sentryTrace = isPageloadTransaction ? getMetaContent('sentry-trace') : '';
      var baggage = isPageloadTransaction ? getMetaContent('baggage') : '';

      var _tracingContextFromHe = tracingContextFromHeaders(sentryTrace, baggage),
          traceparentData = _tracingContextFromHe.traceparentData,
          dynamicSamplingContext = _tracingContextFromHe.dynamicSamplingContext,
          propagationContext = _tracingContextFromHe.propagationContext;

      var expandedContext = _objectSpread(_objectSpread(_objectSpread({}, context), traceparentData), {}, {
        metadata: _objectSpread(_objectSpread({}, context.metadata), {}, {
          dynamicSamplingContext: traceparentData && !dynamicSamplingContext ? {} : dynamicSamplingContext
        }),
        trimEnd: true
      });

      var modifiedContext = typeof beforeNavigate === 'function' ? beforeNavigate(expandedContext) : expandedContext; // For backwards compatibility reasons, beforeNavigate can return undefined to "drop" the transaction (prevent it
      // from being sent to Sentry).

      var finalContext = modifiedContext === undefined ? _objectSpread(_objectSpread({}, expandedContext), {}, {
        sampled: false
      }) : modifiedContext; // If `beforeNavigate` set a custom name, record that fact

      finalContext.metadata = finalContext.name !== expandedContext.name ? _objectSpread(_objectSpread({}, finalContext.metadata), {}, {
        source: 'custom'
      }) : finalContext.metadata;
      this._latestRouteName = finalContext.name;
      this._latestRouteSource = finalContext.metadata && finalContext.metadata.source;

      if (finalContext.sampled === false) {
        (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log("[Tracing] Will not send ".concat(finalContext.op, " transaction because of beforeNavigate."));
      }

      (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.log("[Tracing] Starting ".concat(finalContext.op, " transaction on scope"));
      var location = WINDOW.location;
      var idleTransaction = startIdleTransaction(hub, finalContext, idleTimeout, finalTimeout, true, {
        location: location
      }, // for use in the tracesSampler
      heartbeatInterval);
      var scope = hub.getScope(); // If it's a pageload and there is a meta tag set
      // use the traceparentData as the propagation context

      if (isPageloadTransaction && traceparentData) {
        scope.setPropagationContext(propagationContext);
      } else {
        // Navigation transactions should set a new propagation context based on the
        // created idle transaction.
        scope.setPropagationContext({
          traceId: idleTransaction.traceId,
          spanId: idleTransaction.spanId,
          parentSpanId: idleTransaction.parentSpanId,
          sampled: !!idleTransaction.sampled
        });
      }

      idleTransaction.registerBeforeFinishCallback(function (transaction) {
        _this2._collectWebVitals();

        addPerformanceEntries(transaction);
      });
      return idleTransaction;
    }
    /** Start listener for interaction transactions */

  }, {
    key: "_registerInteractionListener",
    value: function _registerInteractionListener() {
      var _this3 = this;

      var inflightInteractionTransaction;

      var registerInteractionTransaction = function registerInteractionTransaction() {
        var _this3$options = _this3.options,
            idleTimeout = _this3$options.idleTimeout,
            finalTimeout = _this3$options.finalTimeout,
            heartbeatInterval = _this3$options.heartbeatInterval;
        var op = 'ui.action.click';
        var currentTransaction = getActiveTransaction();

        if (currentTransaction && currentTransaction.op && ['navigation', 'pageload'].includes(currentTransaction.op)) {
          (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.warn("[Tracing] Did not create ".concat(op, " transaction because a pageload or navigation transaction is in progress."));
          return undefined;
        }

        if (inflightInteractionTransaction) {
          inflightInteractionTransaction.setFinishReason('interactionInterrupted');
          inflightInteractionTransaction.finish();
          inflightInteractionTransaction = undefined;
        }

        if (!_this3._getCurrentHub) {
          (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.warn("[Tracing] Did not create ".concat(op, " transaction because _getCurrentHub is invalid."));
          return undefined;
        }

        if (!_this3._latestRouteName) {
          (typeof __SENTRY_DEBUG__ === 'undefined' || __SENTRY_DEBUG__) && logger.warn("[Tracing] Did not create ".concat(op, " transaction because _latestRouteName is missing."));
          return undefined;
        }

        var hub = _this3._getCurrentHub();

        var location = WINDOW.location;
        var context = {
          name: _this3._latestRouteName,
          op: op,
          trimEnd: true,
          metadata: {
            source: _this3._latestRouteSource || 'url'
          }
        };
        inflightInteractionTransaction = startIdleTransaction(hub, context, idleTimeout, finalTimeout, true, {
          location: location
        }, // for use in the tracesSampler
        heartbeatInterval);
      };

      ['click'].forEach(function (type) {
        addEventListener(type, registerInteractionTransaction, {
          once: false,
          capture: true
        });
      });
    }
  }]);

  return BrowserTracing;
}();
/** Returns the value of a meta tag */


function getMetaContent(metaName) {
  // Can't specify generic to `getDomElement` because tracing can be used
  // in a variety of environments, have to disable `no-unsafe-member-access`
  // as a result.
  var metaTag = getDomElement("meta[name=".concat(metaName, "]")); // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access

  return metaTag ? metaTag.getAttribute('content') : undefined;
}

export { BROWSER_TRACING_INTEGRATION_ID, BrowserTracing, getMetaContent };